import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageErrorBoundary } from "@/components/PageErrorBoundary";
import { PageLoadingSpinner } from "@/components/PageLoadingSpinner";

import ProtectedRoute from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/AppLayout";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

// Lazy load all pages
const Login = lazy(() => import("@/pages/Login"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Equipe = lazy(() => import("@/pages/Equipe"));
const PDVs = lazy(() => import("@/pages/PDVs"));
const Regioes = lazy(() => import("@/pages/Regioes"));
const Tarefas = lazy(() => import("@/pages/Tarefas"));
const Campanhas = lazy(() => import("@/pages/Campanhas"));
const Planejamento = lazy(() => import("@/pages/Planejamento"));
const Execucao = lazy(() => import("@/pages/Execucao"));
const Monitoramento = lazy(() => import("@/pages/Monitoramento"));
const Auditoria = lazy(() => import("@/pages/Auditoria"));
const Indicadores = lazy(() => import("@/pages/Indicadores"));
const Relatorios = lazy(() => import("@/pages/Relatorios"));
const Configuracoes = lazy(() => import("@/pages/Configuracoes"));
const AdminUsuarios = lazy(() => import("@/pages/AdminUsuarios"));
const Evidencias = lazy(() => import("@/pages/Evidencias"));
const Desempenho = lazy(() => import("@/pages/Desempenho"));
const Precos = lazy(() => import("@/pages/Precos"));
const Chat = lazy(() => import("@/pages/Chat"));
const Pesquisas = lazy(() => import("@/pages/Pesquisas"));
const LojaPerfeita = lazy(() => import("@/pages/LojaPerfeita"));
const Validade = lazy(() => import("@/pages/Validade"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
      throwOnError: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

const ProtectedPage = ({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: AppRole[] }) => (
  <ProtectedRoute allowedRoles={allowedRoles}>
    <AppLayout>
      <PageErrorBoundary>
        {children}
      </PageErrorBoundary>
    </AppLayout>
  </ProtectedRoute>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <ErrorBoundary>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<PageLoadingSpinner />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor", "visualizacao", "promotor"]}><Dashboard /></ProtectedPage>} />
                <Route path="/equipe" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor"]}><Equipe /></ProtectedPage>} />
                <Route path="/pdvs" element={<ProtectedPage allowedRoles={["admin", "gestor"]}><PDVs /></ProtectedPage>} />
                <Route path="/regioes" element={<ProtectedPage allowedRoles={["admin", "gestor"]}><Regioes /></ProtectedPage>} />
                <Route path="/tarefas" element={<ProtectedPage allowedRoles={["admin", "gestor"]}><Tarefas /></ProtectedPage>} />
                <Route path="/campanhas" element={<ProtectedPage allowedRoles={["admin", "gestor"]}><Campanhas /></ProtectedPage>} />
                <Route path="/planejamento" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor"]}><Planejamento /></ProtectedPage>} />
                <Route path="/execucao" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor", "promotor"]}><Execucao /></ProtectedPage>} />
                <Route path="/monitoramento" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor"]}><Monitoramento /></ProtectedPage>} />
                <Route path="/auditoria" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor"]}><Auditoria /></ProtectedPage>} />
                <Route path="/indicadores" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor", "visualizacao"]}><Indicadores /></ProtectedPage>} />
                <Route path="/relatorios" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor", "visualizacao"]}><Relatorios /></ProtectedPage>} />
                <Route path="/configuracoes" element={<ProtectedPage><Configuracoes /></ProtectedPage>} />
                <Route path="/evidencias" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor", "visualizacao"]}><Evidencias /></ProtectedPage>} />
                <Route path="/desempenho" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor", "visualizacao"]}><Desempenho /></ProtectedPage>} />
                <Route path="/admin/usuarios" element={<ProtectedPage allowedRoles={["admin"]}><AdminUsuarios /></ProtectedPage>} />
                <Route path="/precos" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor", "promotor"]}><Precos /></ProtectedPage>} />
                <Route path="/chat" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor", "promotor"]}><Chat /></ProtectedPage>} />
                <Route path="/pesquisas" element={<ProtectedPage allowedRoles={["admin", "gestor"]}><Pesquisas /></ProtectedPage>} />
                <Route path="/loja-perfeita" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor", "visualizacao"]}><LojaPerfeita /></ProtectedPage>} />
                <Route path="/validade" element={<ProtectedPage allowedRoles={["admin", "gestor", "supervisor", "visualizacao"]}><Validade /></ProtectedPage>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
